import React, { useState } from 'react';
import styled from 'styled-components';
import { ClanNews } from '../../types/clanNews';
import YouTubePlayer from '../common/YouTubePlayer';
import clanNewsService from '../../services/clanNewsService';
import ImageModal from '../common/ImageModal';

interface ClanNewsModalProps {
  news: ClanNews;
  isOpen: boolean;
  onClose: () => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: var(--legacy-card-bg);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 95vh;
    border-radius: 12px;
  }
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 15px;
`;

const NewsTitle = styled.h2`
  color: #00d4ff;
  margin: 0;
  font-size: 1.5rem;
  line-height: 1.3;
  text-shadow: 0 0 15px rgba(0, 212, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const CloseButton = styled.button`
  background: rgba(244, 67, 54, 0.8);
  border: 2px solid #f44336;
  color: #ffffff;
  width: 35px;
  height: 35px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  flex-shrink: 0;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(244, 67, 54, 1);
  }
`;

const NewsMetadata = styled.div`
  padding: 0 20px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  color: #888;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    font-size: 1rem;
  }
`;

const NewsType = styled.span<{ type: 'Public' | 'Internal' }>`
  background: ${props => props.type === 'Public' ? 'rgba(0, 204, 0, 0.2)' : 'rgba(255, 165, 0, 0.2)'};
  border: 1px solid ${props => props.type === 'Public' ? '#00cc00' : '#ffa500'};
  color: ${props => props.type === 'Public' ? '#00cc00' : '#ffa500'};
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: bold;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    padding: 6px 14px;
    font-size: 0.8rem;
  }
`;

const PriorityBadge = styled.span`
  background: rgba(255, 215, 0, 0.2);
  border: 1px solid #ffd700;
  color: #ffd700;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: bold;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    padding: 6px 14px;
    font-size: 0.8rem;
  }
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #b0b0b0;
`;

const ModalContent = styled.div`
  padding: 20px;
`;

const NewsImage = styled.div`
  width: 100%;
  height: 400px;
  background: var(--legacy-card-bg);
  border: 1px solid #00d4ff;
  border-radius: 12px;
  margin-bottom: 20px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
  font-size: 1rem;
  backdrop-filter: blur(10px);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.4);
  }
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const NewsVideo = styled.div`
  margin-bottom: 20px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #00d4ff;
`;

const ContentSection = styled.div`
  margin-bottom: 30px;
`;

const ContentLabel = styled.h3`
  color: #00d4ff;
  font-size: 1.1rem;
  margin-bottom: 15px;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const NewsContent = styled.div`
  font-size: 1rem;
  color: #e0e0e0;
  line-height: 1.8;
  white-space: pre-line;
  word-wrap: break-word;
  background: rgba(26, 35, 46, 0.4);
  padding: 20px;
  border-radius: 8px;
  border: 1px solid rgba(0, 212, 255, 0.2);
  backdrop-filter: blur(5px);
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 15px;
  }
`;

const ClanNewsModal: React.FC<ClanNewsModalProps> = ({ news, isOpen, onClose }) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };
  
  const handleImageClick = () => {
    if (news.imageUrl) {
      setIsImageModalOpen(true);
    }
  };
  
  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
  };

  return (
    <ModalOverlay onClick={handleOverlayClick} onKeyDown={handleKeyDown} tabIndex={0}>
      <ModalContainer>
        <ModalHeader>
          <NewsTitle>{news.title}</NewsTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        
        <NewsMetadata>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <NewsType type={news.type}>{news.type}</NewsType>
            {news.isPriority && <PriorityBadge>Priority</PriorityBadge>}
          </div>
          
          <AuthorInfo>
            <span>By: {news.authorName}</span>
            <span>•</span>
            <span>{clanNewsService.formatDate(news.createdAt)}</span>
          </AuthorInfo>
        </NewsMetadata>
        
        <ModalContent>
          {news.imageUrl && (
            <NewsImage 
              style={{ backgroundImage: `url(${news.imageUrl})` }}
              onClick={handleImageClick}
            >
              {!news.imageUrl && 'NO IMAGE AVAILABLE'}
            </NewsImage>
          )}
          
          {news.videoUrl && (
            <NewsVideo>
              <YouTubePlayer 
                videoUrl={news.videoUrl} 
                autoplay={false}
              />
            </NewsVideo>
          )}
          
          <ContentSection>
            <ContentLabel>Description</ContentLabel>
            <NewsContent>{news.content}</NewsContent>
          </ContentSection>
        </ModalContent>
      </ModalContainer>
      
      {/* Image Modal */}
      {news.imageUrl && (
        <ImageModal
          isOpen={isImageModalOpen}
          imageUrl={news.imageUrl}
          imageAlt={news.title}
          onClose={handleCloseImageModal}
        />
      )}
    </ModalOverlay>
  );
};

export default ClanNewsModal; 